import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import {
  TreeRail,
  WorkspaceBanner,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceHeader,
  WorkspaceLayout,
  WorkspaceNotice,
  WorkspaceTabs,
  useToast,
  workspaceTabIds,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { QuestionnairePreview } from '@/components/secure-link/questionnaire-preview';
import {
  discardDraft,
  discoveryFrameworkKeys,
  draftApi,
  frameworkReason,
  getFrameworkContent,
  isStaleDraftError,
  publishFramework,
  startDraft,
  useFrameworkContentQuery,
  useFrameworksQuery,
  useQuestionnairePreviewQuery,
  type ContentMode,
  type DraftWriteResult,
  type ExistingItem,
  type FrameworkContent,
} from '@/lib/api/discovery-frameworks';
import { AreaPanel } from './area-panel';
import { ReferenceMapView } from './reference-map/reference-map-view';
import { AreasTab } from './areas-tab';
import { InterviewTypesTab, QuestionnairesTab } from './card-tabs';
import { DifferencesList, NewFrameworkDialog, PublishDialog } from './dialogs';
import { tryAdd, type DraftWrite, type EditorCtx } from './editor';
import {
  changeSummary,
  contentCounts,
  draftChangeCount,
  draftDifferences,
  formatDate,
  messageOf,
  plural,
  type DraftDifference,
  type FrameworkTab,
} from './framework-logic';
import { refusalOf } from './parts';
import { QuestionnairePanel } from './questionnaire-panel';
import { HistoryTab, RulesTab } from './rules-history';
import { TypePanel } from './type-panel';

/**
 * Firm settings → Discovery frameworks (T-0184, E12-F01).
 *
 * Design reference: `frameworksView()` and its panels in
 * `docs/discovery/prototype/discovery.js` (route `#frameworks`), built from
 * the shared workspace components like the requirement library screen.
 *
 * Reading is open to every firm user. A curator (template curation
 * permission, Decision 54.1) chooses Edit to open the framework's one
 * draft, or join it if another curator already has (rule 2); every change
 * then saves to the draft as it is typed. Writes run one at a time, each
 * against the revision the last one left. When someone else saved first
 * the server refuses (`framework.draft_changed`): nothing is overwritten,
 * the draft is reloaded and the curator is shown what changed. Publish
 * asks for a change note; discard asks first.
 *
 * The framework and the tab live in the URL; the route owns them.
 */

export interface DiscoveryFrameworksScreenProps {
  frameworkId: string | null;
  tab: FrameworkTab;
  /** A process whose reference map is open in place of the areas (T-0189). */
  processId?: string | null;
  onFrameworkChange: (id: string) => void;
  onTabChange: (tab: FrameworkTab) => void;
  onProcessChange?: (id: string | null) => void;
}

type Panel = { kind: 'area' | 'type' | 'questionnaire'; id: string } | null;
interface StaleNotice {
  /** The draft is gone: published or discarded by someone else. */
  gone: boolean;
  differences: DraftDifference[];
}

/** A queued write that was overtaken by a reload of the draft; it is dropped. */
class Superseded extends Error {}
const noop = () => undefined;
const TABS_ID = 'discovery-framework';

export function DiscoveryFrameworksScreen({
  frameworkId,
  tab,
  processId = null,
  onFrameworkChange,
  onTabChange,
  onProcessChange = noop,
}: DiscoveryFrameworksScreenProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const listQuery = useFrameworksQuery();
  const frameworks = React.useMemo(() => listQuery.data?.frameworks ?? [], [listQuery.data]);
  const isCurator = listQuery.data?.can_curate === true;
  const selected = frameworks.find((f) => f.id === frameworkId) ?? frameworks[0] ?? null;
  const selectedId = selected?.id ?? null;
  const selectedIdRef = React.useRef(selectedId);
  selectedIdRef.current = selectedId;

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const editing = isCurator && selectedId != null && editingId === selectedId;
  const contentQuery = useFrameworkContentQuery(selectedId, editing ? 'draft' : 'current');
  const content = contentQuery.data;
  const draftReady = editing && content?.version?.state === 'draft';

  const [panel, setPanel] = React.useState<Panel>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [stale, setStale] = React.useState<StaleNotice | null>(null);
  const [pending, setPending] = React.useState(0);
  const [generation, setGeneration] = React.useState(0);
  const [starting, setStarting] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [publishError, setPublishError] = React.useState<string | null>(null);
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [newOpen, setNewOpen] = React.useState(false);
  const [preview, setPreview] = React.useState<{ id: string; mode: ContentMode } | null>(null);
  const generationRef = React.useRef(0);
  const queueRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const busy = pending > 0;

  const previewQuery = useQuestionnairePreviewQuery(
    preview && selectedId
      ? { frameworkId: selectedId, questionnaireId: preview.id, mode: preview.mode }
      : null,
  );

  // ---- draft writes ------------------------------------------------------

  /** Run `fn` after the writes before it, with the draft revision they left. */
  const queued = React.useCallback(
    <T,>(fwId: string, fn: (base: number) => Promise<T>): Promise<T> => {
      const gen = generationRef.current;
      setPending((n) => n + 1);
      const task = queueRef.current.then(() => {
        if (gen !== generationRef.current) throw new Superseded();
        const base = qc.getQueryData<FrameworkContent>(
          discoveryFrameworkKeys.content(fwId, 'draft'),
        )?.version?.draft_revision;
        if (base == null) throw new Error('The draft is still loading. Try again in a moment.');
        return fn(base);
      });
      queueRef.current = task.then(noop, noop);
      return task.finally(() => setPending((n) => n - 1));
    },
    [qc],
  );

  /**
   * Someone else saved to the draft first (rule 2). Drop the writes still
   * waiting, reload the draft, and show what changed; if the draft is gone,
   * go back to the published version.
   */
  const reloadAfterStale = React.useCallback(
    async (fwId: string) => {
      generationRef.current += 1;
      setGeneration(generationRef.current);
      const key = discoveryFrameworkKeys.content(fwId, 'draft');
      const before = qc.getQueryData<FrameworkContent>(key);
      await qc.cancelQueries({ queryKey: key });
      try {
        const after = await qc.fetchQuery({
          queryKey: key,
          queryFn: () => getFrameworkContent(fwId, 'draft'),
          staleTime: 0,
        });
        setStale({
          gone: false,
          differences: before?.version?.state === 'draft' ? draftDifferences(before, after) : [],
        });
      } catch (err) {
        if (frameworkReason(err) === 'framework.no_draft') {
          setEditingId(null);
          setPublishOpen(false);
          setStale({ gone: true, differences: [] });
        } else setSaveError(messageOf(err, 'The draft could not be reloaded.'));
      }
      void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.list() });
      void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.content(fwId, 'current') });
    },
    [qc],
  );

  const write = React.useCallback<DraftWrite>(
    (fn, options) => {
      const fwId = selectedIdRef.current;
      if (!fwId) return Promise.resolve(null);
      const key = discoveryFrameworkKeys.content(fwId, 'draft');
      return queued(fwId, async (base) => {
        const result = await fn(fwId, base);
        qc.setQueryData<FrameworkContent>(key, (prev) =>
          prev?.version
            ? { ...prev, version: { ...prev.version, draft_revision: result.draft_revision } }
            : prev,
        );
        void qc.invalidateQueries({ queryKey: key });
        return result;
      }).then(
        (result): DraftWriteResult => {
          setSaveError(null);
          return result;
        },
        (err: unknown) => {
          if (err instanceof Superseded) return null;
          if (isStaleDraftError(err)) void reloadAfterStale(fwId);
          else if (options.onError) options.onError(err);
          else setSaveError(`${options.what} was not saved: ${messageOf(err, 'it was refused.')}`);
          return null;
        },
      );
    },
    [qc, queued, reloadAfterStale],
  );

  // ---- navigation and lifecycle ------------------------------------------

  const selectFramework = (id: string) => {
    setEditingId(null);
    setPanel(null);
    setSaveError(null);
    setStale(null);
    onFrameworkChange(id);
  };
  const selectTab = (next: FrameworkTab) => {
    setPanel(null);
    onTabChange(next);
  };

  const startEditing = async () => {
    if (!selected) return;
    setSaveError(null);
    setStale(null);
    setStarting(true);
    try {
      await startDraft(selected.id);
    } catch (err) {
      // A draft already exists: join it (rule 2, scenario 3).
      if (frameworkReason(err) !== 'framework.draft_exists') {
        setSaveError(`The draft could not be opened: ${messageOf(err, 'it was refused.')}`);
        setStarting(false);
        return;
      }
    }
    setStarting(false);
    void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.list() });
    setEditingId(selected.id);
  };

  /** After publish or discard: back to the published version. */
  const leaveDraft = (fwId: string) => {
    setEditingId(null);
    setStale(null);
    setSaveError(null);
    void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.all, refetchType: 'none' });
    void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.list() });
    qc.removeQueries({ queryKey: discoveryFrameworkKeys.content(fwId, 'draft') });
  };

  const publish = (changeNote: string) => {
    if (!selected) return;
    const fwId = selected.id;
    setPublishing(true);
    setPublishError(null);
    queued(fwId, (base) => publishFramework(fwId, base, changeNote)).then(
      (result) => {
        setPublishing(false);
        setPublishOpen(false);
        leaveDraft(fwId);
        toast({
          message: `Published v${result.version}. New interview types and questions are available now in running engagements, for interviews added from today. Rules and areas stay on each engagement's copy.`,
          durationMs: 10_000,
        });
      },
      (err: unknown) => {
        setPublishing(false);
        if (err instanceof Superseded) return;
        // The dialog stays open on the reloaded draft, with what changed.
        if (isStaleDraftError(err)) void reloadAfterStale(fwId);
        else setPublishError(messageOf(err, 'The framework could not be published.'));
      },
    );
  };

  const discard = () => {
    if (!selected) return;
    const fwId = selected.id;
    queued(fwId, (base) => discardDraft(fwId, base)).then(
      () => {
        setDiscardOpen(false);
        leaveDraft(fwId);
        toast({
          message:
            selected.current_version > 0
              ? `Draft discarded. ${selected.name} is back to v${selected.current_version}.`
              : 'Draft discarded.',
        });
      },
      (err: unknown) => {
        setDiscardOpen(false);
        if (err instanceof Superseded) return;
        if (isStaleDraftError(err)) void reloadAfterStale(fwId);
        else setSaveError(`The draft was not discarded: ${messageOf(err, 'it was refused.')}`);
      },
    );
  };

  // ---- adds: new items open in their panel -------------------------------

  const openExisting = (kind: NonNullable<Panel>['kind']) => (item: ExistingItem) =>
    setPanel({ kind, id: item.id });
  const restoreExisting =
    (kind: NonNullable<Panel>['kind']) =>
    async (item: ExistingItem): Promise<void> => {
      const restore = (id: string, base: number) =>
        kind === 'area'
          ? draftApi.patchArea(id, base, item.id, { status: 'active' })
          : kind === 'type'
            ? draftApi.patchInterviewType(id, base, item.id, { status: 'active' })
            : draftApi.patchQuestionnaire(id, base, item.id, { status: 'active' });
      if (await write(restore, { what: item.code ?? item.name })) {
        setPanel({ kind, id: item.id });
        toast({ message: `${item.code ?? item.name} restored in the draft.` });
      }
    };
  const add =
    (
      kind: NonNullable<Panel>['kind'],
      fn: (id: string, base: number, name: string) => Promise<DraftWriteResult>,
      idOf: (r: DraftWriteResult) => string | undefined,
    ) =>
    async (name: string): Promise<React.ReactNode | null> => {
      const { result, refusal } = await tryAdd(
        write,
        (id, base) => fn(id, base, name),
        name,
        (err) => refusalOf(err, { open: openExisting(kind), restore: restoreExisting(kind) }),
      );
      const id = result ? idOf(result) : undefined;
      if (id) {
        setPanel({ kind, id });
        toast({ message: `${result?.code ?? ''} ${name} added to the draft.`.trim() });
      }
      return refusal;
    };

  // ---- states before the screen can render -------------------------------

  if (listQuery.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <ErrorMessage error={listQuery.error} />
        <WorkspaceButton onClick={() => void listQuery.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!listQuery.data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading discovery frameworks…
      </div>
    );
  }

  // ---- header, banner, rail ----------------------------------------------

  const currentVersion = selected?.current_version ?? 0;
  const nextVersion = currentVersion + 1;
  const publishedSources = selected?.sources_required ?? null;
  const changeCount = content && draftReady ? draftChangeCount(content, publishedSources) : 0;
  const hasChanges = draftReady && (content?.version?.has_changes ?? changeCount > 0);

  const header = (
    <WorkspaceHeader
      title="Discovery frameworks"
      meta={
        // Kept narrow so the actions stay beside the title in the settings pane.
        <span className="block max-w-[560px]">
          The method engagements follow: areas, interview types, questionnaires and rules. An
          engagement copies a version when discovery starts, so later edits never change running
          work.
        </span>
      }
      actions={
        isCurator && selected ? (
          editing ? (
            <>
              <WorkspaceButton
                size="md"
                variant="tertiary"
                disabled={!draftReady || busy}
                onClick={() => setDiscardOpen(true)}
              >
                Discard draft
              </WorkspaceButton>
              <WorkspaceButton
                size="md"
                variant="primary"
                disabled={!hasChanges || busy}
                title={hasChanges ? undefined : 'Nothing to publish yet'}
                onClick={() => {
                  setPublishError(null);
                  setStale(null);
                  setPublishOpen(true);
                }}
              >
                Publish v{nextVersion}…
              </WorkspaceButton>
            </>
          ) : (
            <>
              <WorkspaceButton size="md" onClick={() => setNewOpen(true)}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                New framework
              </WorkspaceButton>
              <WorkspaceButton
                size="md"
                variant="primary"
                disabled={starting}
                onClick={() => void startEditing()}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                {starting ? 'Opening…' : 'Edit'}
              </WorkspaceButton>
            </>
          )
        ) : null
      }
    />
  );

  const banner = editing ? (
    <WorkspaceBanner>
      <span className="flex items-start gap-2.5">
        <Pencil className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
        <span>
          {contentQuery.isError ? (
            <>
              <strong className="font-semibold text-ink-900">The draft could not be loaded.</strong>{' '}
              {messageOf(contentQuery.error, '')}{' '}
              <button
                type="button"
                className="font-medium underline underline-offset-[3px]"
                onClick={() => void contentQuery.refetch()}
              >
                Try again
              </button>
            </>
          ) : !draftReady ? (
            'Opening the draft…'
          ) : (
            <>
              <strong className="font-semibold text-ink-900">Draft v{nextVersion}.</strong>{' '}
              {currentVersion > 0
                ? `Engagements keep using v${currentVersion} until you publish, and running engagements stay on their copy after that.`
                : 'Engagements can start on this framework once you publish it.'}{' '}
              {changeCount > 0
                ? `${plural(changeCount, 'change')} so far.`
                : 'Nothing to publish yet.'}
            </>
          )}
        </span>
      </span>
    </WorkspaceBanner>
  ) : null;

  const rail = (
    <>
      <TreeRail
        aria-label="Frameworks"
        categories={[]}
        items={frameworks.map((f) => ({
          id: f.id,
          label: f.name,
          meta: f.current_version > 0 ? `v${f.current_version}` : 'draft',
          dot: isCurator && f.draft != null,
          dotLabel: 'Unpublished draft',
        }))}
        activeId={selectedId}
        onSelect={selectFramework}
      />
      <div role="separator" className="mx-2 my-3 h-px bg-ink-100" />
      <p className="px-2 text-meta text-ink-500">
        Seeded from Caliber v1, with state-specific wording removed. Statutory packs come later, as
        requirement library modules.
      </p>
    </>
  );

  // ---- main column -------------------------------------------------------

  const ctx: EditorCtx | null = content ? { content, editing: draftReady, busy, write } : null;
  const refProcess =
    ctx && tab === 'areas' && processId
      ? (ctx.content.processes.find((p) => p.id === processId) ?? null)
      : null;
  const counts = content ? contentCounts(content) : null;
  const meta = selected
    ? [
        currentVersion > 0
          ? `Published ${formatDate(selected.published_at)}${
              selected.published_by?.name ? ` by ${selected.published_by.name}` : ''
            }`
          : 'Not published yet',
        `used by ${plural(selected.engagements_using, 'engagement')}`,
        ...(counts
          ? [
              plural(counts.areas, 'area'),
              plural(counts.interviewTypes, 'interview type'),
              `${plural(counts.questionnaires, 'questionnaire')}, ${plural(counts.questions, 'question')}`,
            ]
          : []),
        ...(content?.version?.sources_required === 1 ? ['corroboration is turned off'] : []),
      ].join(' · ')
    : '';

  const tabs = counts
    ? [
        { value: 'areas' as const, label: 'Areas', count: counts.areas },
        { value: 'types' as const, label: 'Interview types', count: counts.interviewTypes },
        { value: 'questionnaires' as const, label: 'Questionnaires', count: counts.questionnaires },
        { value: 'rules' as const, label: 'Rules' },
        { value: 'history' as const, label: 'History' },
      ]
    : [];

  let body: React.ReactNode;
  if (!selected) {
    body = (
      <p className="px-6 py-14 text-center text-body-sm text-ink-500">
        No discovery frameworks yet.
      </p>
    );
  } else if (contentQuery.isError && !editing) {
    body = (
      <div className="p-6">
        <ErrorMessage error={contentQuery.error} />
      </div>
    );
  } else if (!ctx) {
    body = (
      <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading {selected.name}…
      </div>
    );
  } else if (refProcess) {
    // A reference process opens in place of the areas table, with the same
    // map canvas and chat as engagements (prototype `refProcessView()`).
    body = (
      <ReferenceMapView
        key={`${refProcess.id}:${generation}`}
        ctx={ctx}
        frameworkId={selected.id}
        process={refProcess}
        areaLabel={ctx.content.areas.find((a) => a.id === refProcess.area_id)?.label ?? ''}
        versionLabel={draftReady ? `draft v${nextVersion}` : `v${currentVersion}`}
        canStartDraft={isCurator && !editing}
        onStartDraft={() => void startEditing()}
        onBack={() => onProcessChange(null)}
      />
    );
  } else {
    const selectedOf = (kind: NonNullable<Panel>['kind']) =>
      panel?.kind === kind ? panel.id : null;
    body = (
      <div
        role="tabpanel"
        id={workspaceTabIds(TABS_ID, tab).panel}
        aria-labelledby={workspaceTabIds(TABS_ID, tab).tab}
        className="flex min-h-0 flex-1 flex-col"
      >
        {tab === 'areas' ? (
          <AreasTab
            ctx={ctx}
            isCurator={isCurator}
            selectedId={selectedOf('area')}
            onOpen={(id) => setPanel({ kind: 'area', id })}
            onAdd={add('area', draftApi.addArea, (r) => r.area_id)}
          />
        ) : tab === 'types' ? (
          <InterviewTypesTab
            ctx={ctx}
            isCurator={isCurator}
            selectedId={selectedOf('type')}
            onOpen={(id) => setPanel({ kind: 'type', id })}
            onAdd={add('type', draftApi.addInterviewType, (r) => r.interview_type_id)}
          />
        ) : tab === 'questionnaires' ? (
          <QuestionnairesTab
            ctx={ctx}
            isCurator={isCurator}
            selectedId={selectedOf('questionnaire')}
            onOpen={(id) => setPanel({ kind: 'questionnaire', id })}
            onAdd={add('questionnaire', draftApi.addQuestionnaire, (r) => r.questionnaire_id)}
          />
        ) : tab === 'rules' ? (
          <RulesTab ctx={ctx} />
        ) : (
          <HistoryTab frameworkId={selected.id} />
        )}
      </div>
    );
  }

  let panelNode: React.ReactNode = null;
  if (ctx && panel && !refProcess) {
    const close = () => setPanel(null);
    const key = `${panel.id}:${generation}`;
    if (panel.kind === 'area') {
      const area = ctx.content.areas.find((a) => a.id === panel.id);
      if (area)
        panelNode = (
          <AreaPanel
            key={key}
            ctx={ctx}
            area={area}
            onClose={close}
            onOpenProcess={(id) => {
              setPanel(null);
              onProcessChange(id);
            }}
          />
        );
    } else if (panel.kind === 'type') {
      const type = ctx.content.interview_types.find((t) => t.id === panel.id);
      if (type) panelNode = <TypePanel key={key} ctx={ctx} type={type} onClose={close} />;
    } else {
      const q = ctx.content.questionnaires.find((x) => x.id === panel.id);
      if (q) {
        panelNode = (
          <QuestionnairePanel
            key={key}
            ctx={ctx}
            questionnaire={q}
            onClose={close}
            onPreview={() => setPreview({ id: q.id, mode: draftReady ? 'draft' : 'current' })}
          />
        );
      }
    }
  }

  return (
    <>
      <WorkspaceLayout header={header} banner={banner} rail={rail} railLabel="Discovery frameworks">
        {selected ? (
          <div className="flex-none">
            <div className="px-6 pb-2.5 pt-3.5">
              <h2 className="text-h2 font-semibold text-ink-900">
                {selected.name}{' '}
                <span className="text-meta font-medium text-ink-500">
                  {currentVersion > 0 ? `v${currentVersion}` : 'not published yet'}
                </span>
              </h2>
              <p className="text-meta text-ink-500">{meta}</p>
            </div>
            {tabs.length > 0 ? (
              <WorkspaceTabs
                aria-label="Framework sections"
                idPrefix={TABS_ID}
                tabs={tabs}
                value={tab}
                onChange={selectTab}
                className="border-b-0 px-6"
              />
            ) : null}
          </div>
        ) : null}
        {/* The panel opens over (or beside) the tab content only: the
            framework's name and its tabs stay in reach. */}
        <WorkspaceLayout panel={panelNode}>
          {saveError || stale ? (
            <div className="flex-none px-6 pt-3">
              <WorkspaceNotice tone={saveError ? 'critical' : 'at-risk'}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    {saveError ?? (
                      <>
                        <strong className="font-semibold text-ink-900">
                          {stale?.gone
                            ? 'This draft was published or discarded by someone else.'
                            : 'The draft changed: someone else saved to it first.'}
                        </strong>{' '}
                        {stale?.gone
                          ? 'Your last change was not saved. You are looking at the published version; choose Edit to start again.'
                          : 'Your last change was not saved, and nothing of theirs was overwritten. The draft has been reloaded. Check what changed, then make your change again.'}
                        {stale && stale.differences.length > 0 ? (
                          <div className="mt-2">
                            <DifferencesList items={stale.differences} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    className="flex-none font-medium underline underline-offset-[3px]"
                    onClick={() => {
                      setSaveError(null);
                      setStale(null);
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </WorkspaceNotice>
            </div>
          ) : null}
          {body}
        </WorkspaceLayout>
      </WorkspaceLayout>

      {preview ? (
        <QuestionnairePreview
          data={previewQuery.data}
          error={previewQuery.error}
          ofDraft={preview.mode === 'draft'}
          onClose={() => setPreview(null)}
        />
      ) : null}

      {isCurator ? (
        <>
          <NewFrameworkDialog
            open={newOpen}
            onOpenChange={setNewOpen}
            frameworks={frameworks}
            defaultSourceId={selectedId}
            onOpenExisting={(id) => {
              setNewOpen(false);
              selectFramework(id);
            }}
            onCreated={(id, name) => {
              setNewOpen(false);
              void qc.invalidateQueries({ queryKey: discoveryFrameworkKeys.list() });
              onFrameworkChange(id);
              setPanel(null);
              setEditingId(id);
              toast({
                message: `${name} created as a draft. Change what differs, then publish it.`,
              });
            }}
          />
          {selected && content ? (
            <PublishDialog
              open={publishOpen}
              onOpenChange={setPublishOpen}
              frameworkName={selected.name}
              nextVersion={nextVersion}
              corroborationOff={content.version?.sources_required === 1}
              summary={changeSummary(content, publishedSources)}
              stale={stale && !stale.gone ? stale.differences : null}
              publishing={publishing}
              error={publishError}
              onPublish={publish}
            />
          ) : null}
          {selected ? (
            <WorkspaceDialog
              open={discardOpen}
              onOpenChange={setDiscardOpen}
              title={`Discard the draft of ${selected.name}?`}
              description="This cannot be undone."
              actions={
                <>
                  <WorkspaceButton size="md" onClick={() => setDiscardOpen(false)}>
                    Keep the draft
                  </WorkspaceButton>
                  <WorkspaceButton
                    size="md"
                    variant="destructive"
                    disabled={busy}
                    onClick={discard}
                  >
                    Discard draft
                  </WorkspaceButton>
                </>
              }
            >
              <p className="text-body-sm text-ink-700">
                {changeCount > 0
                  ? `${plural(changeCount, 'change')} will be removed and the`
                  : 'The'}{' '}
                framework goes back to{' '}
                {currentVersion > 0 ? `version ${currentVersion}` : 'having no published version'}.
                Codes given to new items in this draft are not reused.
              </p>
            </WorkspaceDialog>
          ) : null}
        </>
      ) : null}
    </>
  );
}
